import { initializeApp, deleteApp, getApps } from 'firebase/app';
import {
  getAuth, setPersistence, inMemoryPersistence,
  createUserWithEmailAndPassword, deleteUser, sendPasswordResetEmail, signOut,
} from 'firebase/auth';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  writeBatch, addDoc, serverTimestamp, arrayUnion, arrayRemove, deleteField,
} from 'firebase/firestore';
import { db, firebaseConfig } from '../config/firebase';

/**
 * Firebase mechanics for the Employee Management tab — creating and removing a
 * person across Firebase Auth and Firestore.
 *
 * Kept out of App.js and out of the React components on purpose: none of this
 * touches component state, and it is long enough that inlining it would bury the
 * shell. Nothing here throws; every function returns a result object so a failed
 * row can be reported in place instead of replacing the whole app with the
 * full-screen permission error.
 */

/** Permission levels the app actually checks. Any other `role` is a job title. */
export const PERMISSION_ROLES = ['Admin', 'L1 Team Lead', 'L2 Team Lead'];

/**
 * Every collection that can hold data underneath a user, spelled out.
 *
 * This has to be hardcoded. `listCollections()` is Admin-SDK-only, and the
 * `{period}` documents are *phantom* — they carry no fields of their own, so a
 * query cannot discover them even though `evaluations/{period}/cards/{cardType}`
 * holds real documents. `getDocs()` on a fully-specified path does work from the
 * browser, so enumerating is the only complete option. Verified against 46 real
 * user document trees: `cards`, `ikrs` and `status` hold data, `meeting` is
 * written by EvaluationsView, and nothing exists below the leaf documents.
 *
 * Do not "simplify" this into a query — it will silently stop finding data.
 */
export const EVAL_SUBCOLLECTIONS = ['evaluations', 'evaluations_fy2627'];
export const EVAL_PERIODS = ['Q1', 'Q2', 'Q3', 'Q4', 'Annual'];
export const EVAL_LEAF_COLLECTIONS = ['cards', 'ikrs', 'status', 'meeting'];

/** Firestore caps a batch at 500 writes; leave headroom. */
const BATCH_LIMIT = 400;
/** How many leaf collections to scan at once. */
const SCAN_CONCURRENCY = 8;

/** Every `users/{uid}/{sub}/{period}/{leaf}` collection path for one person. */
const evalLeafPaths = (uid) => {
  const paths = [];
  EVAL_SUBCOLLECTIONS.forEach(sub => EVAL_PERIODS.forEach(period => (
    EVAL_LEAF_COLLECTIONS.forEach(leaf => paths.push(`users/${uid}/${sub}/${period}/${leaf}`))
  )));
  return paths;
};

/** Every phantom `users/{uid}/{sub}/{period}` document path for one person. */
const evalPeriodPaths = (uid) => {
  const paths = [];
  EVAL_SUBCOLLECTIONS.forEach(sub => EVAL_PERIODS.forEach(period => (
    paths.push(`users/${uid}/${sub}/${period}`)
  )));
  return paths;
};

/** Drop keys whose value is undefined — Firestore rejects them outright. */
const definedOnly = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/** Run `worker` over `items` a few at a time rather than all at once. */
const mapLimited = async (items, limit, worker) => {
  const out = [];
  for (let i = 0; i < items.length; i += limit) {
    out.push(...await Promise.all(items.slice(i, i + limit).map(worker)));
  }
  return out;
};

/**
 * A long random password the admin never sees or types. The new user sets their
 * own via the reset email, and at this length `auth/weak-password` (the app's
 * six-character floor) cannot happen.
 */
export const generateTempPassword = (length = 20) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => chars[b % chars.length]).join('');
};

/** `krCode` → the objective and KR it names, for rendering `accessibleKRs`. */
export const buildKrCodeIndex = (okrsFY2627 = []) => {
  const index = new Map();
  (okrsFY2627 || []).forEach(objective => (objective.keyResults || []).forEach(kr => {
    if (kr?.code) {
      index.set(kr.code, {
        objectiveCode: objective.code || '',
        objectiveTitle: objective.objective || '',
        krText: kr.text || '',
      });
    }
  }));
  return index;
};

/**
 * Resolve a user's `accessibleKRs` for display: blanks dropped (real documents
 * carry a stray ''), duplicates collapsed, numeric-aware sort so O10 follows O2,
 * and codes whose KR no longer exists flagged rather than hidden.
 */
export const resolveAccessibleKRs = (accessibleKRs, index) => (
  [...new Set((accessibleKRs || []).map(code => String(code || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map(code => {
      const hit = index.get(code);
      return hit ? { code, ...hit, unknown: false } : { code, unknown: true };
    })
);

/**
 * Create a person: a Firebase Auth account plus their `users/{uid}` profile.
 *
 * The Auth account is created on a throwaway secondary Firebase app so the
 * signed-in admin is never swapped out. Order matters: Auth first (it is what
 * mints the uid the profile must be keyed by), then the profile, and if the
 * profile write fails the Auth account is deleted again so no orphan login
 * survives. That rollback is only possible because the new user is signed in on
 * the secondary app and authenticated seconds ago — deleting your own account
 * needs neither the Admin SDK nor a re-auth.
 *
 * @param {Object} form         { name, email, role, managerEmail }
 * @param {?Object} managerDoc  the manager's user document, when resolvable
 * @returns {Promise<Object>} { ok, uid, warning } or { ok: false, code, message, orphanUid }
 */
export const createEmployee = async ({ name, email, role, managerEmail, managerDoc }) => {
  const cleanEmail = String(email || '').trim().toLowerCase();
  const appName = `employee-admin-${Date.now()}`;
  let secondaryApp = null;
  let created = null;
  let warning;

  try {
    // A unique name per call, so a previously failed deleteApp cannot collide.
    secondaryApp = getApps().find(a => a.name === appName) || initializeApp(firebaseConfig, appName);
    const secondaryAuth = getAuth(secondaryApp);

    // Before the create, not after. Persistence is already keyed by
    // apiKey:appName so the admin's session is isolated regardless, but this
    // also keeps the new user's credential out of localStorage/IndexedDB
    // entirely, so a crash mid-flow cannot leave a stray session behind.
    await setPersistence(secondaryAuth, inMemoryPersistence);

    created = await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, generateTempPassword());
    const uid = created.user.uid;

    try {
      // The PRIMARY db, deliberately. A Firestore built on secondaryApp would
      // authenticate as the brand-new user, who is not an admin and has no
      // profile yet, so firestore.rules would reject the create. setDoc rather
      // than addDoc because the document id has to BE the auth uid.
      await setDoc(doc(db, 'users', uid), definedOnly({
        name: String(name || '').trim(),
        email: cleanEmail,
        role: String(role || '').trim(),
        managerEmail: managerEmail ? String(managerEmail).trim().toLowerCase() : undefined,
        // The uid, not just the email: firestore.rules matches a manager on
        // managerId, so without this their manager cannot review them.
        managerId: managerDoc?.id || undefined,
        teamId: managerDoc?.teamId || undefined,
        team: managerDoc?.team || undefined,
        accessibleKRs: [],
        directReports: [],
        createdAt: serverTimestamp(),
      }));
    } catch (profileError) {
      console.error('Profile write failed, rolling back the auth account:', profileError);
      try {
        await deleteUser(created.user);
      } catch (rollbackError) {
        console.error('Rollback failed — orphaned auth account:', uid, rollbackError);
        return {
          ok: false,
          code: 'orphan-auth',
          orphanUid: uid,
          orphanEmail: cleanEmail,
          message: `A login was created for ${cleanEmail} (uid ${uid}) but its profile could not be saved, `
            + 'and the login could not be removed. Add the address to scripts/deleteUsersList.txt and run '
            + '`npm run users:delete` to clear it.',
        };
      }
      return {
        ok: false,
        code: profileError.code || 'profile-write-failed',
        message: profileError.code === 'permission-denied'
          ? 'Firestore rejected the profile write — your admin role could not be confirmed. The login was rolled back.'
          : 'The profile could not be saved, so the login was rolled back. Please try again.',
      };
    }

    // Only once the profile exists, so nobody is invited to an account they
    // cannot use. A failure here leaves a perfectly good account behind.
    try {
      await sendPasswordResetEmail(secondaryAuth, cleanEmail);
    } catch (mailError) {
      console.error('Password-reset email failed:', mailError);
      warning = 'reset-email-failed';
    }

    // Manager mode is gated on the manager having direct reports, so a missing
    // link here means a real person their manager cannot evaluate.
    if (managerDoc?.id) {
      try {
        const managerRef = doc(db, 'users', managerDoc.id);
        const managerSnap = await getDoc(managerRef);
        const current = managerSnap.data()?.directReports || [];
        if (!current.includes(uid)) {
          await updateDoc(managerRef, { directReports: [...current, uid] });
        }
      } catch (linkError) {
        console.error('Could not add the new user to their manager\'s direct reports:', linkError);
        warning = 'manager-link-failed';
      }
    }

    return { ok: true, uid, warning };
  } catch (error) {
    console.error('Employee creation failed:', error);
    const messages = {
      'auth/email-already-in-use':
        'A login already exists for this address but no profile was found. The browser cannot look up another '
        + 'account\'s uid, so the old login has to go first: add the address to scripts/deleteUsersList.txt, run '
        + '`npm run users:delete`, then try again.',
      'auth/invalid-email': 'That is not a valid email address.',
      'auth/weak-password': 'The generated password was rejected. Please try again.',
      'auth/admin-restricted-operation':
        'Account creation is turned off for this Firebase project. Enable Authentication → Settings → '
        + 'User actions → "Enable create (sign-up)".',
      'auth/too-many-requests': 'Firebase is rate-limiting account creation. Wait a minute and try again.',
      'auth/network-request-failed': 'Network error — nothing was created. Please try again.',
    };
    return {
      ok: false,
      code: error.code || 'unknown',
      message: messages[error.code] || `Could not create the user (${error.code || error.message}).`,
    };
  } finally {
    // Each swallowed separately so cleanup can never mask the real failure.
    try { if (secondaryApp) await signOut(getAuth(secondaryApp)); } catch (e) { /* best effort */ }
    try { if (secondaryApp) await deleteApp(secondaryApp); } catch (e) { /* best effort */ }
  }
};

/**
 * How far up a reporting line to walk before giving up on the cycle check. Real
 * lines are a handful deep; the cap only exists so pre-existing bad data cannot
 * spin this forever.
 */
const MANAGER_CHAIN_LIMIT = 20;

/** The uids between `startId` and the top of its reporting line, `startId` excluded. */
const managerChainIds = (startId, users) => {
  const chain = [];
  const seen = new Set();
  let currentId = startId;

  const byId = new Map((users || []).map(u => [u.uid || u.id, u]));

  for (let hop = 0; hop < MANAGER_CHAIN_LIMIT && currentId; hop += 1) {
    if (seen.has(currentId)) break;
    seen.add(currentId);
    const nextId = byId.get(currentId)?.managerId || null;
    if (!nextId) break;
    chain.push(nextId);
    currentId = nextId;
  }

  return chain;
};

/**
 * Set, change, or clear a person's manager.
 *
 * The other half of `createEmployee`, which until now was the only writer of
 * `managerId` and `directReports` anywhere in the app — so a person created with
 * the wrong manager, or none, could only be fixed in the Firestore console.
 *
 * Three documents move together in one batch, because each one alone is a broken
 * state somebody would hit: `managerId` is what firestore.rules matches on, so
 * without it the manager is denied the evaluation; `directReports` is what gates
 * manager mode in EvaluationsView, so without it the person never appears in the
 * manager's list; and a stale entry on the old manager leaves them able to see
 * somebody who no longer reports to them.
 *
 * `managerEmail` is written alongside the id because the roster, the exports and
 * the notification Cloud Function all still read it.
 *
 * @param {Object}  args
 * @param {string}  args.uid            the person whose manager is changing
 * @param {?Object} args.newManagerDoc  the new manager's user document, or null to clear
 * @param {Array}   args.users          the live roster, for validation and the old link
 * @returns {Promise<Object>} { ok, managerId, managerEmail } or { ok: false, code, message }
 */
export const updateEmployeeManager = async ({ uid, newManagerDoc, users }) => {
  const roster = Array.isArray(users) ? users : [];
  const employee = roster.find(u => (u.uid || u.id) === uid);
  const newManagerId = newManagerDoc ? (newManagerDoc.uid || newManagerDoc.id) : null;
  const previousManagerId = employee?.managerId || null;

  if (!uid) {
    return { ok: false, code: 'missing-uid', message: 'No employee was selected.' };
  }
  if (newManagerId && newManagerId === uid) {
    return { ok: false, code: 'self-manager', message: 'Somebody cannot be their own manager.' };
  }
  if (newManagerId === previousManagerId) {
    return { ok: false, code: 'unchanged', message: 'That is already their manager.' };
  }
  // A cycle would make the reporting line infinite: the OKR and evaluation views
  // walk it, and manager mode would show each of them the other's evaluation.
  if (newManagerId && managerChainIds(newManagerId, roster).includes(uid)) {
    const name = employee?.name || employee?.email || 'this person';
    return {
      ok: false,
      code: 'cycle',
      message: `${newManagerDoc.name || newManagerDoc.email} already reports up through ${name}, `
        + 'so this would create a loop in the reporting line.',
    };
  }

  const newManagerEmail = newManagerDoc
    ? String(newManagerDoc.email || '').trim().toLowerCase()
    : null;
  if (newManagerDoc && !newManagerEmail) {
    return {
      ok: false,
      code: 'manager-without-email',
      message: 'That manager has no email address on their profile, so they could not be notified. '
        + 'Add one first.',
    };
  }

  try {
    const batch = writeBatch(db);

    batch.update(doc(db, 'users', uid), newManagerDoc
      ? { managerId: newManagerId, managerEmail: newManagerEmail }
      // deleteField rather than null: EmployeeDetailPanel and the Cloud Function
      // both test for presence, and a null would read as "set, but blank".
      : { managerId: deleteField(), managerEmail: deleteField() });

    // Guarded on the roster: a batch.update against a document that no longer
    // exists fails the whole commit, and a dangling managerId is exactly the
    // data problem this function exists to repair.
    if (previousManagerId && roster.some(u => (u.uid || u.id) === previousManagerId)) {
      batch.update(doc(db, 'users', previousManagerId), { directReports: arrayRemove(uid) });
    }
    if (newManagerId && roster.some(u => (u.uid || u.id) === newManagerId)) {
      batch.update(doc(db, 'users', newManagerId), { directReports: arrayUnion(uid) });
    }

    await batch.commit();
    return { ok: true, managerId: newManagerId, managerEmail: newManagerEmail };
  } catch (error) {
    console.error('Could not update the employee\'s manager:', error);
    return {
      ok: false,
      code: error.code || 'manager-update-failed',
      message: error.code === 'permission-denied'
        ? 'Firestore rejected the change — your admin role could not be confirmed.'
        : `Could not save the manager (${error.code || error.message}).`,
    };
  }
};

/**
 * Remove a person's Firestore footprint: every evaluation document beneath them,
 * the phantom period documents, and finally their `users/{uid}` profile.
 *
 * Leaves are deleted before the profile, and a failure anywhere in that first
 * phase aborts before the profile is touched. If the profile went first — or went
 * at all after a partial failure — the person would vanish from every list in the
 * app and their leftover data would be unreachable from any UI; only the Admin SDK
 * could find it again. Aborting instead leaves the row visible and the whole
 * operation safe to re-run, since a second pass simply finds less to delete.
 *
 * `profileDeleted` in the result says whether the person is actually gone, so the
 * caller can withdraw the queued auth-cleanup entry when nothing was removed.
 *
 * Their Firebase Auth account is NOT touched: the browser cannot delete another
 * user's login. `queueAuthCleanup` records it so the existing
 * `scripts/deleteUsers.js` can finish the job.
 *
 * @returns {Promise<Object>} { ok, profileDeleted, deletedDocs, skippedPeriodDocs, errors }
 */
export const deleteEmployeeCompletely = async ({ uid, viewerUid, onProgress }) => {
  if (!uid) return { ok: false, profileDeleted: false, errors: [{ stage: 'guard', message: 'No user id given.' }] };
  if (uid === viewerUid) {
    return { ok: false, profileDeleted: false, errors: [{ stage: 'guard', message: 'You cannot delete your own account.' }] };
  }

  const errors = [];
  let deletedDocs = 0;
  let skippedPeriodDocs = 0;

  try {
    // --- scan every enumerable leaf collection ---
    const refLists = await mapLimited(evalLeafPaths(uid), SCAN_CONCURRENCY, async (path) => {
      try {
        const snap = await getDocs(collection(db, path));
        return snap.docs.map(d => d.ref);
      } catch (error) {
        errors.push({ stage: 'scan', path, message: error.message });
        return [];
      }
    });
    const refs = refLists.flat();
    if (onProgress) onProgress({ stage: 'scan', found: refs.length });

    // --- delete the leaves, sequentially so a failure stops somewhere known ---
    for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
      const chunk = refs.slice(i, i + BATCH_LIMIT);
      const batch = writeBatch(db);
      chunk.forEach(ref => batch.delete(ref));
      try {
        await batch.commit();
        deletedDocs += chunk.length;
        if (onProgress) onProgress({ stage: 'delete', done: deletedDocs, total: refs.length });
      } catch (error) {
        errors.push({ stage: 'delete-leaves', message: error.message });
      }
    }

    // --- the phantom period documents ---
    for (const path of evalPeriodPaths(uid)) {
      try {
        await deleteDoc(doc(db, path));
      } catch (error) {
        // These documents hold no fields, so failing to remove one changes
        // nothing observable. Never let it abort a delete that has otherwise
        // succeeded.
        skippedPeriodDocs += 1;
      }
    }

    // --- the profile itself, last, and only if everything above succeeded ---
    // Scan and batch failures are collected rather than thrown, so without this
    // the profile was deleted even when none of the evaluation data had been —
    // orphaning that data and signing the person out of an app they still work in.
    if (errors.length > 0) {
      return { ok: false, profileDeleted: false, deletedDocs, skippedPeriodDocs, errors };
    }

    await deleteDoc(doc(db, 'users', uid));
    if (onProgress) onProgress({ stage: 'done' });

    return { ok: true, profileDeleted: true, deletedDocs, skippedPeriodDocs, errors };
  } catch (error) {
    console.error('Employee deletion failed:', error);
    errors.push({ stage: 'delete-user-doc', message: error.message });
    return { ok: false, profileDeleted: false, deletedDocs, skippedPeriodDocs, errors };
  }
};

/**
 * Record a login that still needs removing with the Admin SDK. Written before
 * the profile is deleted, so an interrupted delete still leaves the trail — and
 * in Firestore rather than sessionStorage, so it survives a closed tab and every
 * admin sees the same list.
 */
export const queueAuthCleanup = async ({ uid, email, name, deletedBy }) => {
  try {
    const ref = await addDoc(collection(db, 'authCleanupQueue'), definedOnly({
      uid, email, name: name || undefined, deletedBy: deletedBy || undefined,
      deletedAt: serverTimestamp(), resolved: false,
    }));
    return { ok: true, id: ref.id };
  } catch (error) {
    console.error('Could not queue auth cleanup:', error);
    return { ok: false, message: error.message };
  }
};

/**
 * Withdraw an entry queued for a delete that then failed. The queue is written
 * before anything is destroyed, so an abort would otherwise leave a banner
 * claiming a profile is gone when it is still there — and inviting an admin to
 * delete the login of someone who still uses the app.
 */
export const withdrawAuthCleanup = async (id) => {
  if (!id) return { ok: false, message: 'No entry id.' };
  try {
    await deleteDoc(doc(db, 'authCleanupQueue', id));
    return { ok: true };
  } catch (error) {
    console.error('Could not withdraw auth cleanup entry:', error);
    return { ok: false, message: error.message };
  }
};

/** Mark a queued login as dealt with once the script has been run. */
export const resolveAuthCleanup = async (id) => {
  try {
    await updateDoc(doc(db, 'authCleanupQueue', id), { resolved: true });
    return { ok: true };
  } catch (error) {
    console.error('Could not resolve auth cleanup entry:', error);
    return { ok: false, message: error.message };
  }
};
